import type React from 'react';
import { t } from 'i18next';

import { renderFeedbackIcon, renderToastIcon } from './toastIcons';
import { dismissToast, showToast, type ToastKind, type ToastPosition } from './toastStore';

/** The id a toast gets when the caller does not name one. */
const DEFAULT_TOAST_ID = 'opto-info';

export interface ToastOptions {
    /** A toast with the same id that is still open is not shown twice. */
    toastId?: string;
    position?: ToastPosition;
    /** Milliseconds before the toast closes itself, or `false` (the default) to stay. */
    autoClose?: number | false;
    closeOnClick?: boolean;
    hideProgressBar?: boolean;
    /** A toast always pauses its countdown while hovered or focused. */
    pauseOnHover?: true;
    /** The border colour, when it should differ from the status. */
    type?: ToastKind;
    onClose?: () => void;
}

export interface ToastTemplateBackendFeedbackProps {
    httpStatusCode?: number;
    toastId?: string;
    content: string | React.ReactNode;
    position?: ToastPosition;
}

/** Confirms a feedback submission, or says it failed when the request did not succeed. */
function ToastTemplateBackendFeedback({
    httpStatusCode = 200,
    toastId = 'default',
    content,
}: ToastTemplateBackendFeedbackProps) {
    const succeeded = httpStatusCode === 200 || httpStatusCode === 201;
    showToast({
        id: toastId,
        content: succeeded ? content : t('molecules.toastTemplate.feedbackFailed'),
        kind: succeeded ? 'success' : 'error',
        icon: succeeded ? renderToastIcon('success', 'success') : renderFeedbackIcon('error'),
        // Five seconds like every success toast; a failure stays longer to be read.
        autoClose: succeeded ? 5000 : 8000,
        closeOnClick: true,
        showProgress: true,
    });

    return <></>;
}

interface ToastInfoTemplateProps {
    content: string | React.ReactNode;
    position?: ToastPosition;
    type?: ToastKind;
    module?: 'lice' | 'health' | 'fishspeed' | 'general';
    /**
     * A route segment the toast belongs to: raising a toast elsewhere closes the default
     * toast, and the toast closes when the route leaves the segment.
     */
    page?: string;
    license?: string;
    icon:
        | 'noicon'
        | 'lice'
        | 'health'
        | 'fishspeed'
        | 'optoscale'
        | 'winch'
        | 'Laksvel'
        | 'video'
        | 'new_winch'
        | 'success'
        | 'error'
        | 'lux'
        | string;
    role?: string;
    options?: ToastOptions;
    location?: Pick<Location, 'pathname'>;
    status?: ToastKind;
    /** A localStorage key: once the toast is closed it is not shown again in this browser. */
    toastDismissKey?: string;
}

function ToastInfo({
    content,
    type,
    page = 'all',
    icon = 'noicon',
    location = window.location,
    options = {},
    status = 'info',
    toastDismissKey,
}: ToastInfoTemplateProps): void {
    if (toastDismissKey && localStorage.getItem(toastDismissKey) === 'true') {
        return;
    }

    const onPage = location.pathname.split('/').includes(page);
    if (!onPage) {
        dismissToast(DEFAULT_TOAST_ID);
    }

    const {
        toastId = DEFAULT_TOAST_ID,
        autoClose = false,
        closeOnClick = true,
        hideProgressBar = false,
        type: kind = type ?? status,
        onClose,
    } = options;

    showToast({
        id: toastId,
        content,
        // The border follows the status unless a caller asks for another one.
        kind,
        icon: renderToastIcon(icon, status),
        autoClose,
        closeOnClick,
        showProgress: autoClose !== false && !hideProgressBar,
        page: page === 'all' ? undefined : page,
        onClose: () => {
            if (toastDismissKey) localStorage.setItem(toastDismissKey, 'true');
            onClose?.();
        },
    });
}

/**
 * Says an action failed, in a toast that closes itself after five seconds. A toast with the
 * same id that is still open is not shown twice.
 */
function ToastError(content: React.ReactNode, toastId: string): void {
    ToastInfo({
        content,
        status: 'error',
        icon: 'noicon',
        options: { toastId, position: 'top-right', autoClose: 5000 },
    });
}

interface ToastSuccessOptions {
    /** Milliseconds before it closes; five seconds unless a caller has reason to differ. */
    autoClose?: number;
    /** Replaces the check mark, e.g. the winch's own icon. */
    icon?: ToastInfoTemplateProps['icon'];
}

/**
 * Says an action worked, in a toast that closes itself. A toast with the same id that is
 * still open is not shown twice, so each kind of success needs an id of its own.
 */
function ToastSuccess(
    content: React.ReactNode,
    toastId: string,
    options: ToastSuccessOptions = {}
): void {
    const { autoClose = 5000, icon = 'success' } = options;
    ToastInfo({
        content,
        status: 'success',
        icon,
        options: { toastId, position: 'top-right', autoClose },
    });
}

export { ToastError, ToastInfo, ToastSuccess, ToastTemplateBackendFeedback };
